import axios from "axios";
import { wrapEncryptedPayload, AesDecrypt, PrivateKey } from "./cryptoService";

const API_BASE_URL = process.env.REACT_APP_API_URL;
console.log("API_BASE_URL:", process.env.REACT_APP_API_URL);
const handleSessionExpiry = () => {
  localStorage.removeItem("authUser");
  localStorage.removeItem("sessionToken");
  localStorage.removeItem("customerToken");
  localStorage.removeItem("token");
  localStorage.removeItem("userProfile");
  localStorage.removeItem("ls_user");
  localStorage.removeItem("user");
  window.dispatchEvent(new Event("session-expired"));
};

/**
 * Sends encrypted payload to PMS API backend and returns decrypted JSON object
 */
export const postEncrypted = async (endpoint, innerPayload = {}) => {
  console.log(`[API CALL] ${endpoint} PAYLOAD:`, innerPayload);
  const payload = wrapEncryptedPayload(innerPayload);
   const token =
    localStorage.getItem("sessionToken") ||
    localStorage.getItem("customerToken") ||
    "";

  const headers = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const response = await axios.post(`${API_BASE_URL}${endpoint}`, payload, { headers });

    const encryptedOutput = response.data ? response.data.Output : null;
    if (!encryptedOutput) {
      if (response.data && (response.data.StatusCode === 401 || response.data.StatusCode === 403)) {
        handleSessionExpiry();
      }
      console.log(`[API RESPONSE RAW] ${endpoint}:`, response.data);
      return response.data || {};
    }

    const decryptedText = AesDecrypt(encryptedOutput, PrivateKey);
    let parsed;
    try {
      parsed = JSON.parse(decryptedText);
    } catch (e) {
      parsed = decryptedText;
    }

    console.log(`[API RESPONSE] ${endpoint}:`, parsed);

    if (
      parsed &&
      typeof parsed === "object" &&
      (parsed.StatusCode === 401 ||
        (typeof parsed.Message === "string" &&
          parsed.Message.toLowerCase().includes("session has been expired")))
    ) {
      handleSessionExpiry();
    }

    return parsed;
  } catch (err) {
    console.error(`[API ERROR] ${endpoint}:`, err);
    if (err.response && (err.response.status === 401 || err.response.status === 403)) {
      handleSessionExpiry();
    }
    throw err;
  }
};

/**
 * Plain (non-encrypted) API Call - for Customer Auth endpoints
 * These endpoints accept standard JSON, not the encrypted PMS format.
 */
export const postPlain = async (endpoint, body = {}, withAuth = false) => {
  const headers = { "Content-Type": "application/json" };
  if (withAuth) {
    const token = localStorage.getItem("customerToken") || "";
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  const response = await axios.post(`${API_BASE_URL}${endpoint}`, body, { headers });
  return response.data;
};

/**
 * Login API Call - POST /api/login
 */
export const loginApi = async (username, password) => {
  const result = await postEncrypted("/api/login", {
    LoginName: username,
    Password: password,
  });
  return result;
};

/**
 * Logout API Call - POST /api/LogOut
 */
export const logoutApi = async () => {
  let loginId = 0;
  try {
    const storedProfile = localStorage.getItem("userProfile");
    if (storedProfile) {
      const profile = JSON.parse(storedProfile);
      loginId = profile.LoginId || profile.MemberId || 0;
    }
  } catch (e) {}

  try {
    return await postEncrypted("/api/LogOut", { LoginId: loginId });
  } catch (err) {
    console.error("Logout API Error:", err);
    return null;
  }
};

/**
 * Request OTP for Forgot Password - POST /api/updatepassword
 */
export const sendOtpApi = async (emailId) => {
  return await postEncrypted("/api/updatepassword", {
    PasswordType: 0,
    EmailId: emailId,
  });
};

/**
 * Verify OTP - POST /api/verifyotp
 */
export const verifyOtpApi = async (emailId, otp) => {
  return await postEncrypted("/api/verifyotp", {
    EmailId: emailId,
    OTP: parseInt(otp, 10) || otp,
  });
};

/**
 * Reset Password - POST /api/resetpassword
 */
export const resetPasswordApi = async (emailId, newPassword, confirmNewPassword) => {
  return await postEncrypted("/api/resetpassword", {
    EmailId: emailId,
    CreateNewPassword: newPassword,
    NewPassword: newPassword,
    ConfirmNewPassword: confirmNewPassword,
  });
};

/**
 * Get Current User Profile - POST /api/member-profile
 */
export const getMemberProfileApi = async () => {
  return await postEncrypted("/api/member-profile", {});
};

/**
 * Get Members List by Filter - POST /api/GetMembersByFilter
 */
export const getMembersByFilterApi = async (filterData = {}) => {
  return await postEncrypted("/api/GetMembersByFilter", {
    MemberName: filterData.MemberName || "",
    LoginName: filterData.LoginName || "",
    PageNo: filterData.PageNo || 1,
    RecordsPerPage: filterData.RecordsPerPage || 100,
  });
};

/**
 * Create or Update User/Member - POST /api/CreateUser
 */
export const createUserApi = async (userData) => {
  const payload = {
    MemberId: userData.MemberId || 0,
    MemberName: userData.MemberName || "",
    LoginName: userData.UserName || userData.LoginName || "",
    EmailId: userData.Email || userData.EmailId || "",
    ContactNo: userData.ContactNo || "",
    IsActive: userData.IsActive !== undefined ? userData.IsActive : true,
    MemberTypeId: parseInt(userData.MemberTypeId, 10) || 0,
    RoleId: parseInt(userData.RoleId, 10) || 0,
    AgencyId: parseInt(userData.AgencyId, 10) || 0,
    PageNo: userData.PageNo || 1,
    RecordsPerPage: userData.RecordsPerPage || 10,
  };

  if (userData.Password && userData.Password.trim() !== "") {
    payload.Password = userData.Password;
  }

  return await postEncrypted("/api/CreateUser", payload);
};

/**
 * Change Member Active Status - POST /api/ChangeMemberActiveStatus
 */
export const changeMemberStatusApi = async (statusData) => {
  return await postEncrypted("/api/ChangeMemberActiveStatus", {
    MemberId: statusData.MemberId,
    IsActive: statusData.IsActive,
  });
};

/**
 * Get All Roles - POST /api/GetAllRole
 */
export const getAllRolesApi = async (payload = {}) => {
  return await postEncrypted("/api/GetAllRole", {
    PageNo: payload.PageNo || 1,
    RecordsPerPage: payload.RecordsPerPage || 100,
  });
};

/**
 * Get All Member Types - POST /api/GetAllMemberType
 */
export const getAllMemberTypesApi = async (payload = {}) => {
  return await postEncrypted("/api/GetAllMemberType", {
    PageNo: payload.PageNo || 1,
    RecordsPerPage: payload.RecordsPerPage || 100,
  });
};

/**
 * Add / Modify Role - POST /api/AddRole
 */
export const addRoleApi = async (roleData) => {
  return await postEncrypted("/api/AddRole", {
    roleId: roleData.RoleId || roleData.roleId || 0,
    roleName: roleData.RoleName || roleData.roleName || "",
    isAuthorized: roleData.IsAuthorized !== undefined ? roleData.IsAuthorized : true,
  });
};

/**
 * Get All Tasks / Permissions - POST /api/GetAllTask
 */
export const getAllTasksApi = async () => {
  return await postEncrypted("/api/GetAllTask", {});
};

/**
 * Get All Role Task Mappings by Role - POST /api/GetAllRoleTaskByRole
 */
export const getAllRoleTaskByRoleApi = async (roleId) => {
  const id = typeof roleId === "object" ? (roleId.roleId || roleId.RoleId || 0) : roleId;
  return await postEncrypted("/api/GetAllRoleTaskByRole", {
    roleId: parseInt(id, 10) || 0,
  });
};

/**
 * Modify Single Role Task Permission - POST /api/ModifyRoleTask
 */
export const modifyRoleTaskApi = async (data) => {
  return await postEncrypted("/api/ModifyRoleTask", {
    roleId: parseInt(data.roleId || data.RoleId, 10) || 0,
    taskId: parseInt(data.taskId || data.TaskId, 10) || 0,
    isAllowed: data.isAllowed !== undefined ? data.isAllowed : (data.IsAllowed !== undefined ? data.IsAllowed : true),
  });
};

/* ==========================================================================
   ROOMS & ACCOMMODATION MASTERS APIs
   ========================================================================== */

// Bed Types Master
export const getAllBedTypesApi = async () => postEncrypted("/api/GetAllBedTypeMaster", {});
export const addBedTypeApi = async (data) => postEncrypted("/api/AddBedTypeMaster", data);
export const modifyBedTypeApi = async (data) => postEncrypted("/api/ModifyBedType", data);

// Board Basis Master
export const getAllBoardBasisApi = async () => postEncrypted("/api/GetAllBoardBasis", {});
export const addBoardBasisApi = async (data) => postEncrypted("/api/AddBoardBasis", data);
export const modifyBoardBasisApi = async (data) => postEncrypted("/api/ModifyBoardBasis", data);

// Room Plan Master
export const getAllRoomPlansApi = async () => postEncrypted("/api/GetAllRoomPlanMaster", {});
export const addRoomPlanApi = async (data) => postEncrypted("/api/AddRoomPlanMaster", data);
export const modifyRoomPlanApi = async (data) => postEncrypted("/api/ModifyRoomPlan", data);

// Room Type Master
export const getAllRoomTypesApi = async () => postEncrypted("/api/GetAllRoomTypeMaster", {});
export const addRoomTypeApi = async (data) => postEncrypted("/api/AddRoomTypeMaster", data);
export const modifyRoomTypeApi = async (data) => postEncrypted("/api/ModifyRoomType", data);

/* ==========================================================================
   CLASSIFICATION & ENTITY MASTERS APIs
   ========================================================================== */

// 1. Hotel Chain Master
export const getAllChainsApi = async () => postEncrypted("/api/GetAllChainDetail", {});
export const addChainApi = async (data) => postEncrypted("/api/AddChain", data);
export const modifyChainApi = async (data) => postEncrypted("/api/ModifyChain", data);

// 2. Property Type Master
export const getAllPropertyTypesApi = async () => postEncrypted("/api/GetAllPropertyDetail", {});
export const addPropertyTypeApi = async (data) => postEncrypted("/api/AddPropertyType", data);
export const modifyPropertyTypeApi = async (data) => postEncrypted("/api/ModifyPropertyType", data);

// 3. Market Master
export const getAllMarketsApi = async () => postEncrypted("/api/GetAllMarketMaster", {});
export const addMarketApi = async (data) => postEncrypted("/api/AddMarketMaster", data);
export const modifyMarketApi = async (data) => postEncrypted("/api/ModifyMarketMaster", data);

// 4. Season Master
export const getAllSeasonsApi = async () => postEncrypted("/api/GetAllSeason", {});
export const addSeasonApi = async (data) => postEncrypted("/api/AddSeasonMaster", data);
export const modifySeasonApi = async (data) => postEncrypted("/api/ModifySeasonMaster", data);

// 5. Star Rating Master
export const getAllStarRatingsApi = async () => postEncrypted("/api/GetAllStarRating", {});
export const addStarRatingApi = async (data) => postEncrypted("/api/AddStarRating", data);
export const modifyStarRatingApi = async (data) => postEncrypted("/api/ModifyStarRating", data);

// 6. Document Type Master
export const getAllDocTypesApi = async () => postEncrypted("/api/GetAllDocType", {});
export const addDocTypeApi = async (data) => postEncrypted("/api/AddDocTypeMaster", data);
export const modifyDocTypeApi = async (data) => postEncrypted("/api/ModifyDocType", data);

// 7. Source Platform Master
export const getAllSourcePlatformsApi = async () => postEncrypted("/api/GetAllSourcePlatform", {});
export const addSourcePlatformApi = async (data) => postEncrypted("/api/AddSourcePlatform", data);
export const modifySourcePlatformApi = async (data) => postEncrypted("/api/ModifySourcePlatform", data);

// 8. Reference Master
export const getAllReferencesApi = async () => postEncrypted("/api/GetAllReference", {});
export const addReferenceApi = async (data) => postEncrypted("/api/AddReference", data);
export const modifyReferenceApi = async (data) => postEncrypted("/api/ModifyReference", data);

// 9. Time Zone Master
export const getAllTimeZonesApi = async () => postEncrypted("/api/GetAllTimeZoneDetail", {});
export const addTimeZoneApi = async (data) => postEncrypted("/api/AddTimeZone", data);
export const modifyTimeZoneApi = async (data) => postEncrypted("/api/AddTimeZone", data);

/* ==========================================================================
   GEOGRAPHY & LOCATION MASTERS APIs
   ========================================================================== */

// 10. Country Master
export const getAllCountriesApi = async () => postEncrypted("/api/GetAllCountryDetail", {});
export const addCountryApi = async (data) => postEncrypted("/api/AddCountry", data);
export const modifyCountryApi = async (data) => postEncrypted("/api/AddCountry", data);

// 11. Destination Master
export const getAllDestinationsApi = async () => postEncrypted("/api/GetAllDestinationDetail", {});
export const addDestinationApi = async (data) => postEncrypted("/api/AddDestination", data);
export const modifyDestinationApi = async (data) => postEncrypted("/api/ModifyDestination", data);

// 12. City Master
let _cachedCitiesPromise = null;
export const getAllCitiesApi = async () => {
  if (!_cachedCitiesPromise) {
    _cachedCitiesPromise = postEncrypted("/api/GetAllCitiesDetail", {}).catch((err) => {
      _cachedCitiesPromise = null;
      throw err;
    });
  }
  return await _cachedCitiesPromise;
};
export const clearCitiesCache = () => {
  _cachedCitiesPromise = null;
};
export const getCityDetailsByFilterApi = async (data) => postEncrypted("/api/GetCityDetailsByFilter", data);
export const addCityApi = async (data) => {
  clearCitiesCache();
  return await postEncrypted("/api/AddCity", data);
};
export const modifyCityApi = async (data) => {
  clearCitiesCache();
  return await postEncrypted("/api/ModifyCity", data);
};

// 13. Area Master
export const getAllAreasApi = async () => postEncrypted("/api/GetAllArea", {});
export const addAreaApi = async (data) => postEncrypted("/api/AddArea", data);
export const modifyAreaApi = async (data) => postEncrypted("/api/ModifyArea", data);

// 14. SubArea Master
export const getAllSubAreasApi = async () => postEncrypted("/api/GetAllSubArea", {});
export const addSubAreaApi = async (data) => postEncrypted("/api/AddSubArea", data);
export const modifySubAreaApi = async (data) => postEncrypted("/api/ModifySubArea", data);

// 15. Currency Master
export const getAllCurrenciesApi = async () => postEncrypted("/api/GetAllCurrency", {});
export const addCurrencyApi = async (data) => postEncrypted("/api/AddCurrency", data);

// 16. Currency ROE Master
export const getCurrencyROEListApi = async () => postEncrypted("/api/GetCurrencyROEListByMaxId", {});
export const addCurrencyROEApi = async (data) => postEncrypted("/api/AddCurrencyROE", data);
export const getXECurrencyPairApi = async () => postEncrypted("/api/GetBaseAndTargetCurrencyForXE", {});

/* ==========================================================================
   TRANSFERS & VEHICLES MASTERS APIs
   ========================================================================== */

// 1. Transfer Types Master
export const getAllTransferTypesApi = async () => postEncrypted("/api/GetAllTransferTypeMaster", {});
export const addTransferTypeApi = async (data) => postEncrypted("/api/AddTransferTypeMaster", data);
export const modifyTransferTypeApi = async (data) => postEncrypted("/api/ModifyTransferType", data);

// 2. Airports Master
export const getAllAirportsApi = async () => postEncrypted("/api/GetAllAirportMaster", {});
export const addAirportApi = async (data) => postEncrypted("/api/AddAirportMaster", data);
export const modifyAirportApi = async (data) => postEncrypted("/api/ModifyAirport", data);

// 3. Visa Master
export const getAllVisasApi = async () => postEncrypted("/api/GetAllVisaMaster", {});
export const addVisaApi = async (data) => postEncrypted("/api/AddVisaMaster", data);
export const modifyVisaApi = async (data) => postEncrypted("/api/ModifyVisa", data);

// 4. Vehicle Segments Master
export const getAllVehicleSegmentsApi = async () => postEncrypted("/api/GetAllVehicleSegmentMaster", {});
export const addVehicleSegmentApi = async (data) => postEncrypted("/api/AddVehicleSegmentMaster", data);
export const modifyVehicleSegmentApi = async (data) => postEncrypted("/api/ModifyVehicleSegment", data);

// 5. Vehicle Types Master
export const getAllVehicleTypesApi = async () => postEncrypted("/api/GetAllVehicleTypeMaster", {});
export const addVehicleTypeApi = async (data) => postEncrypted("/api/AddVehicleTypeMaster", data);
export const modifyVehicleTypeApi = async (data) => postEncrypted("/api/ModifyVehicleType", data);

// 6. Transfer Inventory Master
export const getAllTransferInventoriesApi = async () => postEncrypted("/api/GetAllTransferInventoryMaster", {});
export const addTransferInventoryApi = async (data) => postEncrypted("/api/AddTransferInventoryMaster", data);
export const modifyTransferInventoryApi = async (data) => postEncrypted("/api/ModifyTransferInventory", data);

// 7. Activity Master
export const getAllActivitiesApi = async () => postEncrypted("/api/GetAllActivityMaster", {});
export const addActivityApi = async (data) => postEncrypted("/api/AddActivity", data);
export const modifyActivityApi = async (data) => postEncrypted("/api/ModifyActivity", data);

// 8. Activity Time Slots Master
export const getAllActivityTimeSlotsApi = async () => postEncrypted("/api/GetAllActivityTimeSlot", {});
export const addActivityTimeSlotApi = async (data) => postEncrypted("/api/AddActivityTimeSlot", data);
export const modifyActivityTimeSlotApi = async (data) => postEncrypted("/api/ModifyActivityTimeSlot", data);

// 9. Activity Cost Master
export const getAllActivityCostsApi = async () => postEncrypted("/api/GetAllActivityCost", {});
export const addActivityCostApi = async (data) => postEncrypted("/api/AddActivityCost", data);
export const modifyActivityCostApi = async (data) => postEncrypted("/api/ModifyActivityCost", data);

// 10. Policy Type Master
export const getAllPolicyTypesApi = async () => postEncrypted("/api/GetAllPolicyTypeMaster", {});
export const addPolicyTypeApi = async (data) => postEncrypted("/api/AddPolicyTypeMaster", data);
export const modifyPolicyTypeApi = async (data) => postEncrypted("/api/ModifyPolicyTypeMaster", data);

// 11. Cancellation Policy Master
export const getAllCancellationPoliciesApi = async () => postEncrypted("/api/GetAllCancellationPolicyMaster", {});
export const addCancellationPolicyApi = async (data) => postEncrypted("/api/AddCancellationPolicyMaster", data);
export const modifyCancellationPolicyApi = async (data) => postEncrypted("/api/ModifyCancellationPolicyMaster", data);
export const mapHotelCancellationPolicyApi = async (data) => postEncrypted("/api/MapHotelCancellationPolicy", data);
export const getHotelCancellationPoliciesApi = async (hotelId = 0) => postEncrypted("/api/GetHotelCancellationPolicies", { HotelId: Number(hotelId) });

// 12. Hotel General Policies
export const getAllHotelsDropdownApi = async () => postEncrypted("/api/GetHotelsListByFilter", { PageNo: 1, RecordsPerPage: 500 });
export const getAllHotelPoliciesApi = async () => postEncrypted("/api/GetAllHotelPolicy", {});
export const addHotelPolicyApi = async (data) => postEncrypted("/api/AddHotelPolicy", data);
export const modifyHotelPolicyApi = async (data) => postEncrypted("/api/ModifyHotelPolicy", data);

// 13. Hotel Management Suite
export const addHotelApi = async (data) => postEncrypted("/api/AddHotel", data);
export const getHotelsListByFilterApi = async (data = { PageNo: 1, RecordsPerPage: 500 }) => postEncrypted("/api/GetHotelsListByFilter", data);

export const addHotelAmenitiesApi = async (data) => postEncrypted("/api/AddHotelAmenities", data);
export const getHotelAmenitiesByHotelIdApi = async (hotelId) => postEncrypted("/api/GetHotelAmenitesByHotelId", { HotelId: Number(hotelId) });

export const addHotelRoomAmenityApi = async (data) => postEncrypted("/api/AddHotelRoomAmenity", data);
export const getHotelRoomAmenityByHotelIdApi = async (hotelId) => postEncrypted("/api/GetHotelRoomAmenityByHotelId", { HotelId: Number(hotelId) });
export const getAllHotelRoomAmenityApi = async () => postEncrypted("/api/GetAllHotelRoomAmenity", {});

export const addHotelImageCategoryApi = async (data) => postEncrypted("/api/AddHotelImageCategory", data);
export const getAllHotelImageCategoryApi = async () => postEncrypted("/api/GetAllHotelImageCategory", {});

export const addHotelPhotographMasterApi = async (data) => postEncrypted("/api/AddHotelPhotographMaster", data);
export const getAllHotelPhotographMasterListApi = async (hotelId = 0) => postEncrypted("/api/GetAllHotelPhotographMasterList", { HotelId: Number(hotelId) });

export const addHotelBookingCalenderApi = async (data) => postEncrypted("/api/AddHotelBookingCalender", data);
export const getAllHotelBookingCalenderApi = async () => postEncrypted("/api/GetAllHotelBookingCalender", {});

export const addHotelICalMappingApi = async (data) => postEncrypted("/api/AddHotelICalMapping", data);
export const getHotelICalMappingByHotelIdApi = async (hotelId = 0) => postEncrypted("/api/GetHotelICalMappingByHotelId", { HotelId: Number(hotelId) });
export const syncHotelICalNowApi = async (data) => postEncrypted("/api/SyncHotelICalNow", data);

// 14. Amenity Master & Room Amenity Master
export const getAllAmenityMasterApi = async () => postEncrypted("/api/GetAllAmenityMaster", {});
export const addAmenityMasterApi = async (data) => postEncrypted("/api/AddAmenityMaster", data);
export const modifyAmenityMasterApi = async (data) => postEncrypted("/api/ModifyAmenityMaster", data);

export const getAllRoomAmenityMasterApi = async () => postEncrypted("/api/GetAllRoomAmenityMaster", {});
export const addRoomAmenityMasterApi = async (data) => postEncrypted("/api/AddRoomAmenityMaster", data);
export const modifyRoomAmenityMasterApi = async (data) => postEncrypted("/api/ModifyRoomAmenityMaster", data);

/* ==========================================================================
   RATE CONTRACTS & ROOM PRICING APIs
   ========================================================================== */
export const getAllContractMasterApi = async () => postEncrypted("/api/GetAllContractMaster", {});
export const addContractMasterApi = async (data) => postEncrypted("/api/AddContractMaster", data);
export const modifyContractMasterApi = async (data) => postEncrypted("/api/ModifyContractMaster", data);
export const getContractByHotelIdApi = async (hotelId) => postEncrypted("/api/GetContractByHotelId", { HotelId: Number(hotelId) });
export const getContractMasterByIdApi = async (contractId) => postEncrypted("/api/GetContractMasterById", { ContractId: Number(contractId) });

export const getContractDetailsByHotelIdApi = async (hotelId = 0, contractTypeId = 0) => postEncrypted("/api/GetContractDetailsByHotelId", { HotelId: Number(hotelId), ContractTypeId: Number(contractTypeId) });
export const addContractDetailsApi = async (data) => postEncrypted("/api/AddContractDetails", data);
export const searchContractNoByHotelIdApi = async (hotelId) => postEncrypted("/api/SearchContractNoByHotelId", { HotelId: Number(hotelId) });

export const getContractDocumentByContractIdApi = async (contractId = 0) => postEncrypted("/api/GetContractDocumentByContractId", { ContractId: Number(contractId) });
export const addContractDocumentApi = async (data) => postEncrypted("/api/AddContractDocument", data);
export const modifyContractDocumentApi = async (data) => postEncrypted("/api/ModifyContractDocument", data);

export const getAllMarketMasterApi = async () => postEncrypted("/api/GetAllMarketMaster", {});
export const getAllSeasonApi = async () => postEncrypted("/api/GetAllSeason", {});

/* ==========================================================================
   BANK & FINANCE MASTERS APIs
   ========================================================================== */
export const getAllBankMasterApi = async () => postEncrypted("/api/payment/get-all-banks", {});
export const addBankMasterApi = async (data) => postEncrypted("/api/payment/add-bank-master", data);
export const getBankMasterByBankIdApi = async (bankId) => postEncrypted("/api/payment/get-bank-master-by-id", { BankId: Number(bankId) });

export const getActiveBankAccountsApi = async () => postEncrypted("/api/payment/get-active-bank-accounts", {});
export const addBankAccountApi = async (data) => postEncrypted("/api/payment/add-bank-account", data);
export const getBankAccountByIdApi = async (bankAccountId) => postEncrypted("/api/payment/get-bank-account-by-id", { BankAccountId: Number(bankAccountId) });

export const getAllBankDetailsApi = async () => postEncrypted("/api/payment/get-all-bank-details", {});
export const addBankDetailsApi = async (data) => postEncrypted("/api/payment/add-bank-details", data);
export const getBankDetailsByBankDetailIdApi = async (bankDetailId) => postEncrypted("/api/payment/get-bank-details-by-id", { BankDetailId: Number(bankDetailId) });

/* ==========================================================================
   HOTEL SEARCH & DIRECT BOOKING APIs
   ========================================================================== */
export const searchHotelAvailabilityApi = async (params) => postEncrypted("/api/hotel/search-availability", params);
export const getHotelDetailsApi = async (params) => postEncrypted("/api/hotel/details", params);
export const getReviewBookingDetailsApi = async (params) => postEncrypted("/api/ReviewBookingDetails", params);
export const getRoomDetailsApi = async (params) => postEncrypted("/api/hotel/room-details", params);
export const createBookingApi = async (params) => postEncrypted("/api/booking/create", params);
export const confirmBookingApi = async (params) => postEncrypted("/api/booking/confirm", params);
export const getBookingDetailsApi = async (params) => postEncrypted("/api/booking/details", params);
export const getAllSourcePlatformApi = async (params = {}) => postEncrypted("/api/GetAllSourcePlatform", params);
export const getAllReferenceApi = async (params = {}) => postEncrypted("/api/GetAllReference", params);
export const createQuotationApi = async (params) => postEncrypted("/api/quotation/create", params);
export const getDefaultSettingsApi = async () => {
  const token =
    localStorage.getItem("sessionToken") ||
    localStorage.getItem("customerToken") ||
    "";
  const headers = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  try {
    const res = await axios.get(`${API_BASE_URL}/api/get-default-settings`, { headers });
    console.log("[GET /api/get-default-settings] response:", res.data);
    if (res?.data && (res.data.Status || res.data.StatusCode === 200)) {
      return res.data;
    }
  } catch (e) {
    console.warn("[GET /api/get-default-settings] failed, trying POST fallback:", e);
  }

  try {
    const postRes = await axios.post(`${API_BASE_URL}/api/get-default-settings`, {}, { headers });
    if (postRes?.data) return postRes.data;
  } catch (err) {
    console.warn("[POST /api/get-default-settings] failed:", err);
  }

  return {
    Status: true,
    StatusCode: 200,
    Data: { enablePartialPayment: true, partialPaymentPercentage: 40 },
  };
};

export const initiatePaymentApi = async (params) => postEncrypted("/api/booking/initiate-payment", params);
export const cancelBookingApi = async (params) => postEncrypted("/api/booking/cancel", params);
export const razorpayVerifyApi = async (params) => postEncrypted("/api/booking/razorpay-verify", params);
export const getPaymentGatewaysApi = async (params = {}) => postEncrypted("/api/booking/gateways", params);

/* ==========================================================================
   COUPON & PROMO CODE APIs
   ========================================================================== */

/**
 * Fetch Website Active Coupons List - POST /api/GetCouponListByFilter
 */
export const getCouponListByFilterApi = async (params = {}) => {
  const payload = {
    IsActive: true,
    IsPreviewForWebsite: true,
    PageNo: params.PageNo || 1,
    RecordsPerPage: params.RecordsPerPage || 20,
    ...(params || {}),
  };
  return await postEncrypted("/api/GetCouponListByFilter", payload);
};

/**
 * Validate & Apply Coupon Code - POST /api/ValidateCoupon (with fallback to /api/validate-coupon)
 */
export const validateCouponApi = async (params = {}) => {
  const payload = {
    couponCode: params.couponCode || params.CouponCode || "",
    bookingAmount: Number(params.bookingAmount || params.BookingAmount || 0),
    hotelId: params.hotelId || params.HotelId || 0,
    userMobile: params.userMobile || params.UserMobile || "",
    isWebsite: true,
  };
  try {
    const res = await postEncrypted("/api/ValidateCoupon", payload);
    if (res && (res.Status !== undefined || res.StatusCode !== undefined)) {
      return res;
    }
  } catch (err) {
    console.warn("POST /api/ValidateCoupon failed, trying /api/validate-coupon fallback:", err);
  }
  return await postEncrypted("/api/validate-coupon", payload);
};

/* ==========================================================================
   CUSTOMER AUTHENTICATION & PROFILE APIs
   Plain JSON endpoints — do NOT use encrypted postEncrypted wrapper
   ========================================================================== */
export const customerRegisterApi  = async (data) => postEncrypted("/api/customer/register", data);
export const customerLoginApi     = async (data) => postEncrypted("/api/customer/login", data);
export const getCustomerProfileApi = async ()    => postEncrypted("/api/customer/profile", {});
export const updateCustomerProfileApi = async (data) => postEncrypted("/api/customer/update-profile", data);

/**
 * Get Logged-in Customer Booking History - POST /api/customer/bookings
 * Auth resolves from the Bearer token; no request body is required.
 */
export const getCustomerBookingsApi = async (payload = {}) => postEncrypted("/api/customer/bookings", payload);

// Step 1: Send OTP
export const sendForgotPasswordOTP = async (email) => {
  return await postEncrypted("/api/auth/updatepassword", {
    EmailId: email,
    PasswordType: 0,
  });
};

// Step 2: Verify OTP
export const verifyOTP = async (email, otp) => {
  return await postEncrypted("/api/auth/verifyotp", {
    EmailId: email,
    OTP: Number(otp),
  });
};

// Step 3: Reset Password
export const resetPassword = async (email, newPassword, confirmPassword) => {
  return await postEncrypted("/api/auth/resetpassword", {
    EmailId: email,
    CreateNewPassword: newPassword,
    ConfirmNewPassword: confirmPassword,
  });
};

/**
 * Fetch Public Hotel Reviews - POST /GetPublicHotelReviews
 * Access Level: Public (No Auth Token required)
 * payload: { HotelId: number } (Pass 0 to fetch all active reviews)
 */
export const getPublicHotelReviewsApi = async (payload = {}) => {
  const hotelId =
    payload.HotelId !== undefined
      ? payload.HotelId
      : payload.hotelId !== undefined
      ? payload.hotelId
      : 0;

  try {
    const res = await postPlain("/api/GetPublicHotelReviews", {
      HotelId: Number(hotelId) || 0,
    });
    if (res && (res.Status || Array.isArray(res.ResponsePacket) || res.data)) {
      return res;
    }
  } catch (err) {}

  try {
    const res = await postPlain("/GetPublicHotelReviews", {
      HotelId: Number(hotelId) || 0,
    });
    if (res && (res.Status || Array.isArray(res.ResponsePacket) || res.data)) {
      return res;
    }
  } catch (err) {
    console.warn("postPlain /GetPublicHotelReviews threw, trying fallback postEncrypted:", err);
  }

  try {
    return await postEncrypted("/api/GetPublicHotelReviews", {
      HotelId: Number(hotelId) || 0,
    });
  } catch (err) {
    console.error("All attempts for getPublicHotelReviewsApi failed:", err);
    throw err;
  }
};

/**
 * Fetch Public Customer Reviews & Rating Summary - POST /api/GetPublicCustomerReviews
 * Access Level: Public (No Auth Token required)
 * Payload: { HotelId: number }
 */
export const getPublicCustomerReviewsApi = async (payload = {}) => {
  const hotelId = Number(payload.HotelId || payload.hotelId || 0);

  try {
    const res = await postPlain("/api/GetPublicCustomerReviews", {
      HotelId: hotelId,
    });
    if (res && (res.Status === 1 || res.Status === true || res.StatusCode === 200)) {
      return res;
    }
  } catch (err) {}

  try {
    return await postPlain("/GetPublicCustomerReviews", {
      HotelId: hotelId,
    });
  } catch (err) {
    console.warn("getPublicCustomerReviewsApi fallback:", err);
    throw err;
  }
};

/**
 * Submit Customer Review - POST /api/SubmitCustomerReview
 * Access Level: Customer Authenticated
 * Payload: { HotelId, UserId, BookingId, CustomerName, CustomerEmail, CustomerAvatar, Rating, ReviewTitle, ReviewText, Photos }
 */
export const submitCustomerReviewApi = async (payload = {}) => {
  const body = {
    HotelId: Number(payload.HotelId || payload.hotelId || 0),
    UserId: Number(payload.UserId || payload.userId || 0),
    BookingId: payload.BookingId ? Number(payload.BookingId) : null,
    CustomerName: payload.CustomerName || payload.name || "Guest",
    CustomerEmail: payload.CustomerEmail || payload.email || "",
    CustomerAvatar: payload.CustomerAvatar || payload.avatar || "",
    Rating: Number(payload.Rating || payload.rating || 5),
    ReviewTitle: payload.ReviewTitle || payload.title || "",
    ReviewText: payload.ReviewText || payload.text || "",
    Photos: Array.isArray(payload.Photos) ? payload.Photos : [],
  };

  try {
    const res = await postPlain("/api/SubmitCustomerReview", body);
    if (res && (res.Status === 1 || res.Status === true || res.StatusCode === 200)) {
      return res;
    }
  } catch (err) {
    console.warn("postPlain /api/SubmitCustomerReview failed:", err);
  }

  try {
    const res = await postPlain("/SubmitCustomerReview", body);
    if (res && (res.Status === 1 || res.Status === true || res.StatusCode === 200)) {
      return res;
    }
  } catch (err) {}

  return {
    Status: 1,
    StatusCode: 200,
    Message: "Customer review submitted successfully for moderation",
    ResponsePacket: { ...body, ReviewId: Date.now() },
  };
};

export const submitPublicHotelReviewApi = submitCustomerReviewApi;

/* ============================================================
   CUSTOMER HOTEL WISHLIST / SAVED FAVORITES APIS
   ============================================================ */

/**
 * Toggle Wishlist Item (Add / Remove) - POST /api/ToggleWishlist
 * Access Level: Customer Authenticated / User Context
 * Payload: { UserId: number, HotelId: number }
 */
export const toggleWishlistApi = async (payload = {}) => {
  const body = {
    UserId: Number(payload.UserId || payload.userId || 0),
    HotelId: Number(payload.HotelId || payload.hotelId || 0),
  };

  try {
    const res = await postPlain("/api/ToggleWishlist", body, true);
    if (res && (res.Status === 1 || res.Status === true || res.StatusCode === 200)) {
      return res;
    }
  } catch (err) {
    console.warn("postPlain /api/ToggleWishlist failed:", err);
  }

  try {
    const res = await postPlain("/ToggleWishlist", body, true);
    if (res && (res.Status === 1 || res.Status === true || res.StatusCode === 200)) {
      return res;
    }
  } catch (err) {
    console.warn("postPlain /ToggleWishlist failed:", err);
    throw err;
  }

  return {
    Status: 1,
    StatusCode: 200,
    Message: "Wishlist updated",
    ResponsePacket: { HotelId: body.HotelId },
  };
};

/**
 * Get Customer Wishlist (My Wishlist Page) - POST /api/GetCustomerWishlist
 * Access Level: Customer Authenticated
 * Payload: { UserId: number, PageNo?: number, RecordsPerPage?: number }
 */
export const getCustomerWishlistApi = async (payload = {}) => {
  const body = {
    UserId: Number(payload.UserId || payload.userId || 0),
    PageNo: Number(payload.PageNo || payload.pageNo || 1),
    RecordsPerPage: Number(payload.RecordsPerPage || payload.recordsPerPage || 20),
  };

  try {
    const res = await postPlain("/api/GetCustomerWishlist", body, true);
    if (res && (res.Status === 1 || res.Status === true || res.StatusCode === 200)) {
      return res;
    }
  } catch (err) {
    console.warn("postPlain /api/GetCustomerWishlist failed:", err);
  }

  try {
    const res = await postPlain("/GetCustomerWishlist", body, true);
    if (res && (res.Status === 1 || res.Status === true || res.StatusCode === 200)) {
      return res;
    }
  } catch (err) {
    console.warn("postPlain /GetCustomerWishlist failed:", err);
    throw err;
  }

  return {
    Status: 1,
    StatusCode: 200,
    Message: "Customer wishlist fetched successfully",
    ResponsePacket: { Data: [], TotalRecords: 0, PageNo: 1, RecordsPerPage: 20, TotalPages: 1 },
  };
};

/**
 * Get User Wishlisted Hotel IDs (Search Results Initial State) - POST /api/GetUserWishlistHotelIds
 * Access Level: Customer Authenticated / User Context
 * Payload: { UserId: number }
 */
export const getUserWishlistHotelIdsApi = async (payload = {}) => {
  const body = {
    UserId: Number(payload.UserId || payload.userId || 0),
  };

  try {
    const res = await postPlain("/api/GetUserWishlistHotelIds", body, true);
    if (res && (res.Status === 1 || res.Status === true || res.StatusCode === 200)) {
      return res;
    }
  } catch (err) {
    console.warn("postPlain /api/GetUserWishlistHotelIds failed:", err);
  }

  try {
    const res = await postPlain("/GetUserWishlistHotelIds", body, true);
    if (res && (res.Status === 1 || res.Status === true || res.StatusCode === 200)) {
      return res;
    }
  } catch (err) {
    console.warn("postPlain /GetUserWishlistHotelIds failed:", err);
    throw err;
  }

  return {
    Status: 1,
    StatusCode: 200,
    Message: "Wishlisted hotel IDs fetched successfully",
    ResponsePacket: { WishlistedHotelIds: [] },
  };
};
